import { useState, useEffect, useRef } from "react";
import { captureIP } from "../utils/ipCapture";
import { getSession, saveSession, acquireCaptureLock, releaseCaptureLock, waitForSession } from "../utils/datastoreSession";

/**
 * Captures the visitor's IP and creates/verifies a datastore identity
 * record.  Behaviour is identical to the inline effect previously in
 * AppForm:
 *
 * - When `uniqueID` is falsy, no request is made and `ipData` stays
 *   `null`.
 * - Otherwise, `getSession(uniqueID)` is called first.  On a hit
 *   the resolved object `{ dataid, ip, journeySubmissionFingerprint? }`
 *   is returned directly.
 * - On a miss, a capture lock (`acquireCaptureLock`) is claimed first so a
 *   near-simultaneous second load (another tab, a reload, a link scanner)
 *   waits for and reuses this capture instead of also POSTing a duplicate
 *   datastore record.
 * - `captureIP({ uniqueID })` creates a fresh datastore record.  When that
 *   yields a `dataid`, the session is saved via `saveSession`.
 * - Graceful failure: when the IP or datastore POST fails the hook
 *   exposes `{ dataid: null, ip: null }`.
 */
export function useIpCapture(uniqueID) {
  const [ipData, setIpData] = useState(null);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    if (!uniqueID) return;

    done.current = true;

    (async () => {
      // Try cached session first
      const session = await getSession(uniqueID);
      if (session) {
        setIpData(session);
        return;
      }

      // No valid session yet. If another near-simultaneous load (a second tab, a reload
      // racing an in-flight request, a link scanner) is already capturing for this
      // uniqueID, wait for it instead of also POSTing - avoids creating a duplicate record.
      if (!acquireCaptureLock(uniqueID)) {
        const otherSession = await waitForSession(uniqueID);
        if (otherSession) {
          setIpData(otherSession);
          return;
        }
        // Other attempt didn't finish in time - capture independently rather than block.
      }

      try {
        // Fresh capture
        const data = await captureIP({ uniqueID });
        setIpData(data);

        // Persist the session when a datastore ID exists
        if (data.dataid) {
          await saveSession(uniqueID, data);
        }
      } finally {
        releaseCaptureLock(uniqueID);
      }
    })();
  }, [uniqueID]);

  return ipData;
}
